/**
 * Stockists Filter Web Component
 * 
 * Fetches all stockists data from Storefront API and handles client-side filtering
 * by type (online/onsite) and country. Uses HTML templates for card rendering.
 * 
 * Performance optimizations:
 * - Debounced filter updates
 * - Cached DOM references
 * - Efficient template cloning
 * - Request cancellation support
 * - Optimized filtering with cached cards
 * - localStorage caching with expiry (1 hour) - reduces API calls significantly
 * 
 * Cache behavior:
 * - Data is cached in localStorage for 1 hour
 * - Cache is automatically checked before API calls
 * - Cache is used as fallback if API fails
 * - To force refresh: call element.refreshStockists() or add data-bypass-cache attribute
 * - Cache key includes version - increment cacheVersion to invalidate all caches
 */

class StockistsFilter extends HTMLElement {
  constructor() {
    super();
    this.filterType = 'both'; // 'online', 'onsite', 'both' (default to both - shows all)
    this.filterCountry = '';
    this.typeButtons = null;
    this.countrySelect = null;
    this.countryFilterGroup = null;
    this.stockistsList = null;
    this.emptyState = null;
    this.allCards = null; // Cache all cards for performance
    this.stockistsData = []; // Store all stockists data
    this.template = null; // Cache template reference
    this.abortController = null; // For request cancellation
    this.filterTimeout = null; // For debouncing filter updates
    this.isRendering = false; // Prevent concurrent renders
    this.cacheExpiry = 60 * 60 * 1000; // 1 hour in milliseconds
    this.cacheVersion = 'v1'; // Increment to invalidate all caches
  }

  /**
   * Check if we're in development/debug mode
   * Returns true for localhost, development stores, or when debug flag is set
   */
  isDebugMode() {
    // Check for debug flag in localStorage or URL parameter
    if (localStorage.getItem('stockists-debug') === 'true') {
      return true;
    }
    if (new URLSearchParams(window.location.search).get('stockists-debug') === 'true') {
      return true;
    }
    
    // Check if we're on localhost or development domain
    const hostname = window.location.hostname;
    return hostname === 'localhost' || 
           hostname === '127.0.0.1' || 
           hostname.includes('.myshopify.com');
  }

  /**
   * Get cache key for this section
   */
  getCacheKey(sectionId) {
    return `stockists_${sectionId}_${this.cacheVersion}`;
  }

  /**
   * Get cached stockists data from localStorage
   */
  getCachedStockists(sectionId) {
    try {
      const cacheKey = this.getCacheKey(sectionId);
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) {
        return null;
      }

      const parsed = JSON.parse(cached);
      const now = Date.now();

      // Check if cache is expired
      if (parsed.expiry && now > parsed.expiry) {
        // Remove expired cache
        localStorage.removeItem(cacheKey);
        return null;
      }

      // Return cached data
      return parsed.data || null;
    } catch (error) {
      // localStorage might be disabled or quota exceeded
      if (this.isDebugMode()) {
        console.warn('[Stockists Filter] Error reading from cache:', error);
      }
      return null;
    }
  }

  /**
   * Save stockists data to localStorage
   */
  setCachedStockists(sectionId, data) {
    try {
      const cacheKey = this.getCacheKey(sectionId);
      const cacheData = {
        data: data,
        timestamp: Date.now(),
        expiry: Date.now() + this.cacheExpiry
      };

      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      // localStorage might be disabled or quota exceeded
      // Silently fail - API will still work
      if (this.isDebugMode()) {
        console.warn('[Stockists Filter] Error saving to cache:', error);
      }
    }
  }

  /**
   * Clear cached stockists data (useful for manual refresh)
   */
  clearCachedStockists(sectionId) {
    try {
      const cacheKey = this.getCacheKey(sectionId);
      localStorage.removeItem(cacheKey);
      return true;
    } catch (error) {
      if (this.isDebugMode()) {
        console.warn('[Stockists Filter] Error clearing cache:', error);
      }
      return false;
    }
  }

  /**
   * Force refresh stockists data (bypass cache and fetch from API)
   */
  async refreshStockists() {
    const sectionId = this.getAttribute('data-section-id');
    if (!sectionId) return;

    // Clear cache
    this.clearCachedStockists(sectionId);

    // Fetch fresh data
    try {
      this.stockistsData = await this.fetchAllStockists(sectionId, false);
      
      // Re-populate countries dropdown (for current filter type)
      this.populateCountries(this.stockistsData, this.filterType);
      
      // Re-render cards
      this.renderStockists(this.stockistsData);
      
      // Re-cache cards
      if (this.stockistsList) {
        this.allCards = Array.from(this.stockistsList.querySelectorAll('.stockist-card'));
      }
      
      // Re-apply filters
      this.filterAndRender();
      this.animateCardsIn();
    } catch (error) {
      if (this.isDebugMode()) {
        console.error('[Stockists Filter] Error refreshing stockists:', error);
      }
    }
  }

  async fetchAllStockists(sectionId, useCache = true) {
    // Check cache first if enabled
    if (useCache) {
      const cachedData = this.getCachedStockists(sectionId);
      if (cachedData && Array.isArray(cachedData) && cachedData.length > 0) {
        if (this.isDebugMode()) {
          console.log('[Stockists Filter] Using cached stockists data');
        }
        return cachedData;
      }
    }

    const config = window.stockistsConfig && window.stockistsConfig[sectionId];
    if (!config) {
      if (this.isDebugMode()) {
        console.error('[Stockists Filter] Stockists config not found!');
      }
      return [];
    }

    // Get storefront access token
    const metaTag = document.querySelector('meta[name="storefront-access-token"]');
    const configMetaTag = document.querySelector('meta[name="shopify-storefront-access-token"]');
    const accessToken = config.storefrontAccessToken ||
                       (window.Shopify && window.Shopify.storefrontAccessToken) || 
                       (window.themeVariables && window.themeVariables.storefrontAccessToken) ||
                       (metaTag && metaTag.content) ||
                       (configMetaTag && configMetaTag.content);
    
    if (!accessToken) {
      if (this.isDebugMode()) {
        console.error('[Stockists Filter] Storefront API access token not found.');
      }
      return [];
    }

    // Get shop domain
    const shopDomain = config.shopDomain || 
                      (window.Shopify && window.Shopify.shop) || 
                      (window.location.hostname.includes('.myshopify.com') 
                        ? window.location.hostname 
                        : window.location.hostname + '.myshopify.com');
    const apiUrl = `https://${shopDomain}/api/2024-01/graphql.json`;

    // GraphQL query to fetch all stockists with pagination
    const query = `
      query getStockists($first: Int!, $after: String) {
        metaobjects(type: "stockists", first: $first, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              fields {
                key
                value
              }
            }
          }
        }
      }
    `;

    let allStockists = [];
    let hasNextPage = true;
    let cursor = null;
    const pageSize = 250; // Maximum allowed by Storefront API

    // Create abort controller for request cancellation
    this.abortController = new AbortController();

    try {
      while (hasNextPage) {
        // Check if request was aborted
        if (this.abortController.signal.aborted) {
          return [];
        }

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': accessToken
          },
          body: JSON.stringify({ 
            query, 
            variables: { first: pageSize, after: cursor } 
          }),
          signal: this.abortController.signal
        });

        if (!response.ok) {
          if (this.isDebugMode()) {
            const errorText = await response.text();
            console.error('[Stockists Filter] Storefront API response error:', response.status, errorText);
          }
          break;
        }

        const result = await response.json();
        
        if (result.errors) {
          if (this.isDebugMode()) {
            console.error('[Stockists Filter] GraphQL errors:', result.errors);
          }
          break;
        }

        const metaobjects = result.data?.metaobjects;
        if (!metaobjects) {
          break;
        }

        // Process edges - optimize field mapping
        const edges = metaobjects.edges || [];
        const stockistsBatch = edges.map(edge => {
          const node = edge.node;
          const fields = {};
          
          // Use forEach for better performance than reduce
          node.fields.forEach(field => {
            fields[field.key] = field.value;
          });

          return {
            id: node.id,
            name: fields.name || '',
            type: (fields.type || '').toLowerCase(),
            typeRaw: fields.type || '',
            country: fields.country || '',
            address: fields.address || '',
            url: fields.url || ''
          };
        });

        allStockists.push(...stockistsBatch);

        // Check pagination
        hasNextPage = metaobjects.pageInfo?.hasNextPage || false;
        cursor = metaobjects.pageInfo?.endCursor || null;
      }

      // Save to cache if we got data
      if (allStockists.length > 0) {
        this.setCachedStockists(sectionId, allStockists);
      }

      return allStockists;
    } catch (error) {
      // Don't log abort errors (expected when component unmounts)
      if (error.name !== 'AbortError' && this.isDebugMode()) {
        console.error('[Stockists Filter] Storefront API fetch error:', error);
      }
      
      // Try to return cached data as fallback if API fails
      if (useCache) {
        const cachedData = this.getCachedStockists(sectionId);
        if (cachedData && Array.isArray(cachedData) && cachedData.length > 0) {
          if (this.isDebugMode()) {
            console.log('[Stockists Filter] API failed, using cached data as fallback');
          }
          return cachedData;
        }
      }
      
      return [];
    }
  }

  async connectedCallback() {
    // Get DOM elements
    this.typeButtons = this.querySelectorAll('.type-toggle-button');
    this.countrySelect = this.querySelector('select.select');
    this.countryFilterGroup = this.querySelector('.stockists-filter-group--country');
    this.stockistsList = this.querySelector('.stockists-list');
    this.emptyState = this.querySelector('.stockists-empty-state');
    this.resultsCountAnnouncer = this.querySelector('[id^="stockist-results-count"]');
    
    // Get section ID and card color scheme from data attributes
    const sectionId = this.getAttribute('data-section-id');
    const cardColorScheme = this.getAttribute('data-card-color-scheme') || 'scheme-1';
    const cardColorHash = this.getAttribute('data-card-color-hash') || '';

    // Check for stockists config
    const config = window.stockistsConfig && window.stockistsConfig[sectionId];
    if (!config) {
      console.error('[Stockists Filter] Stockists config not found!');
      return;
    }

    // Announce loading state to screen readers
    if (this.resultsCountAnnouncer) {
      this.resultsCountAnnouncer.textContent = 'Loading stockists...';
    }

    // Check if we should bypass cache (data attribute)
    const bypassCache = this.hasAttribute('data-bypass-cache');
    
    // Fetch all stockists from Storefront API (with cache by default)
    try {
      this.stockistsData = await this.fetchAllStockists(sectionId, !bypassCache);
      
      if (this.stockistsData.length === 0) {
        if (this.stockistsList) {
          const loadingEl = this.stockistsList.querySelector('.stockists-loading');
          if (loadingEl) {
            loadingEl.textContent = 'No stockists available. Please check your Storefront API configuration.';
          } else {
            const emptyDiv = document.createElement('div');
            emptyDiv.className = 'empty-state';
            const p = document.createElement('p');
            p.className = 'text-subdued';
            p.textContent = 'No stockists available. Please check your Storefront API configuration.';
            emptyDiv.appendChild(p);
            this.stockistsList.appendChild(emptyDiv);
          }
        }
        if (this.resultsCountAnnouncer) {
          this.resultsCountAnnouncer.textContent = 'No stockists available.';
        }
        return;
      }

      // Cache template reference for performance
      this.template = document.getElementById(`stockist-card-template-${sectionId}`);
      if (!this.template) {
        console.error('[Stockists Filter] Card template not found!');
        return;
      }
    } catch (error) {
      if (this.isDebugMode()) {
        console.error('[Stockists Filter] Error loading stockists data:', error);
      }
      if (this.stockistsList) {
        const loadingEl = this.stockistsList.querySelector('.stockists-loading');
        if (loadingEl) {
          loadingEl.textContent = 'Error loading stockists. Please check your Storefront API access token in Theme Settings.';
        } else {
          const errorDiv = document.createElement('div');
          errorDiv.className = 'empty-state';
          const p = document.createElement('p');
          p.className = 'text-subdued';
          p.textContent = 'Error loading stockists. Please check your Storefront API access token in Theme Settings.';
          errorDiv.appendChild(p);
          this.stockistsList.appendChild(errorDiv);
        }
      }
      if (this.resultsCountAnnouncer) {
        this.resultsCountAnnouncer.textContent = 'Error loading stockists.';
      }
      return;
    }

    // Store card color scheme
    this.cardColorScheme = cardColorScheme;
    this.cardColorHash = cardColorHash;

    // Extract and populate countries dropdown from all stockists (initially for "both" type)
    this.populateCountries(this.stockistsData, 'both');

    // Render all stockist cards using template
    this.renderStockists(this.stockistsData);

    // Cache all cards after rendering
    if (this.stockistsList) {
      this.allCards = Array.from(this.stockistsList.querySelectorAll('.stockist-card'));
    }

    // Bind event handlers
    this.handleTypeButtonClick = this.handleTypeButtonClick.bind(this);
    this.handleCountryFilterChange = this.handleCountryFilterChange.bind(this);

    // Set up event listeners
    this.typeButtons.forEach(button => {
      button.addEventListener('click', this.handleTypeButtonClick);
    });

    if (this.countrySelect) {
      this.countrySelect.addEventListener('change', this.handleCountryFilterChange);
    }

    // Initial render - default to both (shows all stockists)
    this.updateTypeToggle('both');
    this.filterAndRender();
    
    // Animate cards in after initial render
    this.animateCardsIn();
  }

  disconnectedCallback() {
    // Cancel any pending API requests
    if (this.abortController) {
      this.abortController.abort();
    }

    // Clear any pending timeouts
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }

    // Clean up event listeners
    if (this.typeButtons) {
      this.typeButtons.forEach(button => {
        button.removeEventListener('click', this.handleTypeButtonClick);
      });
    }
    if (this.countrySelect) {
      this.countrySelect.removeEventListener('change', this.handleCountryFilterChange);
    }

    // Clear references to prevent memory leaks
    this.allCards = null;
    this.stockistsData = null;
    this.template = null;
  }

  handleTypeButtonClick(event) {
    const selectedType = event.currentTarget.getAttribute('data-type');
    if (selectedType === 'onsite' || selectedType === 'online' || selectedType === 'both') {
      this.updateTypeToggle(selectedType);
      this.filterType = selectedType;
      
      // Hide/show country filter based on type
      if (this.countryFilterGroup) {
        if (this.filterType === 'online') {
          // Hide country filter for online stores only
          this.countryFilterGroup.classList.add('hidden');
          if (this.countrySelect) {
            this.countrySelect.value = ''; // Reset to "All countries"
            this.filterCountry = '';
          }
        } else {
          // Show country filter for both and onsite (can filter by country)
          this.countryFilterGroup.classList.remove('hidden');
          // Update country dropdown with only available countries for this type
          this.populateCountries(this.stockistsData, selectedType);
        }
      }
      
      // Debounce filter updates for better performance
      this.debouncedFilterAndRender();
    }
  }

  populateCountries(stockistsData, filterType = 'both') {
    if (!this.countrySelect) return;

    // Extract unique countries from stockists filtered by type
    const countriesSet = new Set();
    stockistsData.forEach(stockist => {
      // Only include countries for stockists matching the filter type
      const matchesType = filterType === 'both' 
        ? stockist.type === 'both'
        : stockist.type === filterType;
      
      if (matchesType && stockist.country && stockist.country.trim() !== '') {
        countriesSet.add(stockist.country);
      }
    });

    // Sort countries
    const countries = Array.from(countriesSet).sort();

    // Store current selection to restore if still available
    const currentValue = this.countrySelect.value;

    // Clear existing options
    this.countrySelect.innerHTML = '';
    
    // Add "All countries" option
    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = 'All countries';
    this.countrySelect.appendChild(defaultOption);

    // Add country options
    countries.forEach(country => {
      const option = document.createElement('option');
      option.value = country;
      option.textContent = country;
      this.countrySelect.appendChild(option);
    });

    // Restore previous selection if it's still available, otherwise reset to "All countries"
    if (currentValue && countries.includes(currentValue)) {
      this.countrySelect.value = currentValue;
      this.filterCountry = currentValue;
    } else {
      this.countrySelect.value = '';
      this.filterCountry = '';
    }
  }

  renderStockists(stockistsData) {
    if (!this.stockistsList || !this.template || this.isRendering) return;
    
    this.isRendering = true;

    // Use DocumentFragment for better performance with many cards
    const fragment = document.createDocumentFragment();

    // Pre-compute color scheme classes
    const colorSchemeClasses = `color-scheme--${this.cardColorScheme}${this.cardColorHash ? ` color-scheme--bg-${this.cardColorHash}` : ''}`;

    // Render each stockist card using template
    for (const stockist of stockistsData) {
      const cardFragment = this.template.content.cloneNode(true);
      const card = cardFragment.querySelector('.stockist-card');
      const cardContent = cardFragment.querySelector('.stockist-card__content');
      
      if (!card || !cardContent) continue;

      // Set data attributes for filtering
      card.setAttribute('data-type', stockist.type || '');
      card.setAttribute('data-country', stockist.country || '');

      // Apply color scheme classes
      cardContent.className = `stockist-card__content color-scheme v-stack gap-2 ${colorSchemeClasses}`;

      // Populate card content - cache selectors
      const nameEl = cardFragment.querySelector('.stockist-name');
      const typeBadge = cardFragment.querySelector('.stockist-type-badge');
      const addressEl = cardFragment.querySelector('.stockist-address');
      const countryEl = cardFragment.querySelector('.stockist-country');
      const linkEl = cardFragment.querySelector('.stockist-link');

      // Name
      if (nameEl && stockist.name) {
        nameEl.textContent = stockist.name;
      }

      // Type badge
      if (typeBadge) {
        if (stockist.type === 'both' || stockist.typeRaw?.toLowerCase() === 'both') {
          typeBadge.textContent = 'Onsite/Online';
        } else if (stockist.typeRaw) {
          typeBadge.textContent = stockist.typeRaw;
        } else {
          typeBadge.remove();
        }
      }

      // Address
      if (addressEl) {
        if (stockist.address) {
          addressEl.textContent = stockist.address;
          addressEl.style.whiteSpace = 'pre-line';
        } else {
          addressEl.remove();
        }
      }

      // Country
      if (countryEl) {
        if (stockist.country) {
          countryEl.textContent = stockist.country;
        } else {
          countryEl.remove();
        }
      }

      // Website link - show for all stockists that have a URL
      if (linkEl) {
        if (stockist.url) {
          // Validate URL to prevent XSS
          try {
            new URL(stockist.url);
            linkEl.href = stockist.url;
            linkEl.setAttribute('aria-label', `Visit ${stockist.name || 'website'} (opens in new tab)`);
            linkEl.textContent = 'Visit website';
            linkEl.classList.remove('hidden');
          } catch {
            // Invalid URL, remove link
            linkEl.remove();
          }
        } else {
          linkEl.remove();
        }
      }

      fragment.appendChild(cardFragment);
    }

    // Clear and append in one operation
    this.stockistsList.innerHTML = '';
    this.stockistsList.appendChild(fragment);
    
    this.isRendering = false;
  }

  updateTypeToggle(selectedType) {
    this.typeButtons.forEach(button => {
      const buttonType = button.getAttribute('data-type');
      if (buttonType === selectedType) {
        button.setAttribute('aria-pressed', 'true');
      } else {
        button.setAttribute('aria-pressed', 'false');
      }
    });
  }

  handleCountryFilterChange() {
    if (this.countrySelect) {
      this.filterCountry = this.countrySelect.value || '';
    }
    // Debounce filter updates for better performance
    this.debouncedFilterAndRender();
  }

  debouncedFilterAndRender() {
    // Clear existing timeout
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }

    // Debounce filter updates (50ms delay)
    this.filterTimeout = setTimeout(() => {
      this.filterAndRender();
      // Animate cards in after filter change
      requestAnimationFrame(() => {
        this.animateCardsIn();
      });
    }, 50);
  }

  filterAndRender() {
    if (!this.stockistsList || !this.allCards || this.allCards.length === 0) {
      return;
    }

    let visibleCount = 0;
    const filterCountry = this.filterCountry || '';
    const filterType = this.filterType;

    // Optimize filtering with early returns and cached values
    // Use for loop for better performance than forEach
    for (let i = 0; i < this.allCards.length; i++) {
      const card = this.allCards[i];
      const cardType = card.getAttribute('data-type') || '';
      const cardCountry = card.getAttribute('data-country') || '';

      // Apply type filter (optimized)
      // For "both", only show entries that actually have type "both"
      let matchesType = false;
      if (filterType === 'both' && cardType === 'both') {
        matchesType = true;
      } else if (filterType === cardType) {
        matchesType = true;
      }

      if (!matchesType) {
        card.classList.add('hidden');
        card.classList.remove('visible');
        continue;
      }

      // Apply country filter
      let matchesCountry = true;
      if (filterCountry) {
        if (filterType === 'both') {
          // For "both" type entries, filter by country
          if (cardCountry !== filterCountry) {
            matchesCountry = false;
          }
        } else if (filterType === 'onsite' && cardCountry !== filterCountry) {
          matchesCountry = false;
        }
      }

      // Show/hide card based on filters
      if (matchesCountry) {
        card.classList.remove('hidden');
        card.classList.remove('visible'); // Reset animation state
        visibleCount++;
      } else {
        card.classList.add('hidden');
        card.classList.remove('visible');
      }
    }

    // Show/hide empty state
    if (this.emptyState) {
      if (visibleCount === 0) {
        this.emptyState.classList.remove('hidden');
        if (this.stockistsList) {
          this.stockistsList.style.display = 'none';
        }
      } else {
        this.emptyState.classList.add('hidden');
        if (this.stockistsList) {
          this.stockistsList.style.display = '';
        }
      }
    }

    // Announce results count to screen readers
    if (this.resultsCountAnnouncer) {
      let typeLabel = this.filterType;
      const countryLabel = filterCountry ? ` in ${filterCountry}` : '';
      const countText = visibleCount === 0 
        ? `No ${typeLabel} stockists${countryLabel} found.`
        : `${visibleCount} ${typeLabel} stockist${visibleCount === 1 ? '' : 's'}${countryLabel} found.`;
      
      // Update text content to trigger aria-live announcement
      this.resultsCountAnnouncer.textContent = countText;
    }
  }

  animateCardsIn() {
    if (!this.allCards || this.allCards.length === 0) return;

    // Get all visible cards (not hidden) - use cached array for better performance
    const visibleCards = this.allCards.filter(card => !card.classList.contains('hidden'));
    
    // Animate each card with a staggered delay
    // Limit animation to first 50 cards for performance with large lists
    const cardsToAnimate = visibleCards.slice(0, 50);
    
    cardsToAnimate.forEach((card, index) => {
      // Remove visible class to reset animation state
      card.classList.remove('visible');
      
      // Use requestAnimationFrame twice to ensure the reset is applied before animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            card.classList.add('visible');
          }, index * 50); // 50ms delay between each card
        });
      });
    });

    // For remaining cards beyond 50, show them immediately without animation
    if (visibleCards.length > 50) {
      visibleCards.slice(50).forEach((card) => {
        card.classList.add('visible');
      });
    }
  }
}

// Register the custom element
if (!window.customElements.get('stockists-filter')) {
  window.customElements.define('stockists-filter', StockistsFilter);
}
